import React from 'react';
import {
    IonPage,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
    IonContent,
    IonCard,
    IonCardContent,
    IonItem,
    IonLabel,
    IonList,
    IonThumbnail
} from '@ionic/react';
import { closeOutline, arrowBackOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { RiQrCodeLine, RiSendPlaneLine } from 'react-icons/ri';
import { Asset, PaymentDestinationInfo, WalletInfoState } from '@/types/types';
import { Provider, useSelector } from 'react-redux';
import { dispatch, store } from '@/services/redux/store';
import { useAccountDetailsQuery } from '@/services/api/stellar';
import { useGetQuotesQuery } from '@/services/api/ebioro/ebioro';
import { formatConversion } from '@/services/utils/formatConversion';
import supportedCurrencies from '@/currencies.json';
import { closeModal } from '@/services/redux/slices';
import RecipientPage from '../recipients/RecipientPage';
import { resetPaymentDestinationInfo, setPaymentDestinationInfo } from '@/services/redux/slices/paymentDestinationInfo/paymentDestinationInfo';
import QrCodePageStellarToken from '../qr_code/QrCodePageStellarToken';
import { setSelectedAsset } from '@/services/redux/slices/selected-asset/selected-asset';


const Page: React.FC = () => {
    const { t } = useTranslation('pages/cashAccount');
    const nav = document.querySelector("ion-nav");
    const walletInfo: WalletInfoState = useSelector((state: any) => state.walletInfo);
    const supportedAssets: Asset[] = useSelector((state: any) => state.supportedAssets.supportedAssets);
    const userInfo = useSelector((state: any) => state.user);
    const ebioroAuthToken = useSelector((state: any) => state.sep10AuthToken.tokens[import.meta.env.VITE_API_AUTH_DOMAIN || '']);

    const { data: accountDetails } = useAccountDetailsQuery(walletInfo.account_key, { skip: (!walletInfo.has_key) });

    // Get balances from account details
    const balances = accountDetails?.details?.balances;
    const currentCurrency = userInfo.currency_code || 'USD';

    // Build coins string for quotes API
    let coins = '';
    if (supportedAssets) {
        supportedAssets.forEach((asset: Asset) => {
            if (!coins || coins === '') {
                coins = asset.code;
            } else {
                coins = coins + ',' + asset.code;
            }
        });
    }

    // Get quotes using the same pattern as BalanceCard
    const { data: quotes = [], isSuccess: quotesIsSuccess } = useGetQuotesQuery(
        { coins: coins, authToken: ebioroAuthToken },
        { skip: ebioroAuthToken === '' || ebioroAuthToken === undefined, refetchOnMountOrArgChange: true }
    );

    // Helper functions from BalanceCard
    const formatBalance = (amount: number): string => {
        return formatConversion(amount).toString();
    };

    const getCurrencySymbol = (code: string): string => {
        const currency = supportedCurrencies.find(curr => curr.code === code);
        return currency?.symbol || '$';
    };

    // Dynamic font size based on balance length
    const getDynamicFontSize = (balanceText: string): string => {
        const length = balanceText.length;
        if (length <= 8) return '2rem';        // Normal size for short amounts
        if (length <= 12) return '1.75rem';    // Slightly smaller for medium amounts  
        if (length <= 16) return '1.5rem';     // Smaller for long amounts
        return '1.25rem';                      // Smallest for very long amounts
    };

    const close = () => {

        dispatch(closeModal());

    }

    const goToRecipientPage = (asset: Asset) => {
        if (nav) {
            const destinationInfo: PaymentDestinationInfo = {
                destination_address: undefined,
                destination_amount: undefined,
                destination_asset: asset,
                destination_username: undefined
            }
            dispatch(resetPaymentDestinationInfo());
            dispatch(setPaymentDestinationInfo(destinationInfo));
            nav?.push(() =>
                <RecipientPage
                />
            );
        }
    }

    const presentQRReceiveModal = async (asset: Asset) => {
        dispatch(setSelectedAsset(asset));
        nav?.push(() => <QrCodePageStellarToken />)
    }

    // Calculate total fiat balance using the same logic as BalanceCard but filtered for fiat only
    const totalFiatBalance = React.useMemo((): number => {
        if (!quotesIsSuccess || !userInfo || !balances || !supportedAssets) {
            return 0;
        }

        let totalBalance = 0;

        // Filter and calculate only for fiat assets
        supportedAssets.forEach((asset: Asset) => {
            // Only process fiat assets
            if (asset.type !== 'fiat') return;

            // Check if this asset has a balance
            const assetBalance = balances[asset.identifier];
            if (assetBalance && userInfo) {
                // Look for quotes matching this asset code
                const quoteData = quotes[asset.code];
                if (quoteData) {
                    const quoteValue = quoteData[userInfo.currency_code];
                    if (quoteValue) {
                        const amountToAdd = quoteValue * assetBalance.available.toNumber();
                        totalBalance += amountToAdd;
                    }
                }
            }
        });

        return totalBalance;
    }, [quotesIsSuccess, quotes, userInfo, balances, supportedAssets]);

    return (
        <IonPage>
            <IonHeader className="ion-no-border">
                <IonToolbar>
                    <IonButtons slot='start'>
                        <IonButton onClick={() => { close() }} color='primary'>
                            <IonIcon
                                slot="icon-only"
                                icon={closeOutline}
                            />
                        </IonButton>
                    </IonButtons>
                    <IonTitle>{t('title')}</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding">
                <div className="max-w-2xl mx-auto">
                    {/* Total Balance Card */}
                    <IonCard
                        className="relative overflow-hidden rounded-3xl shadow-2xl border-0 m-0"
                        style={{
                            background: 'linear-gradient(135deg, var(--ion-color-primary) 0%, var(--ion-color-primary-shade) 50%, var(--ion-color-primary-tint) 100%)',
                            '--background': 'transparent',
                        }}
                    >
                        <IonCardContent className="p-6 relative">

                            <div className="relative z-10">
                                <div className="flex flex-col mb-6">
                                    <div className="flex flex-col items-start">
                                        <p className="text-sm font-medium mb-3 text-left" style={{ color: 'white' }}>
                                            {t('totalBalance')}
                                        </p>

                                        {/* Balance Display */}
                                        <div className="flex items-center justify-between w-full" style={{ margin: '24px 0' }}>
                                            <span
                                                className="text-left"
                                                style={{
                                                    fontSize: getDynamicFontSize(`${getCurrencySymbol(currentCurrency)}${formatBalance(totalFiatBalance)}`),
                                                    fontWeight: '900',
                                                    lineHeight: '1.1',
                                                    color: 'white'
                                                }}
                                            >
                                                {getCurrencySymbol(currentCurrency)}{formatBalance(totalFiatBalance)}
                                            </span>
                                        </div>

                                        <p className="text-sm font-medium text-left" style={{ color: 'white', opacity: 0.8 }}>
                                            {currentCurrency} {t('acrossAccounts')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </IonCardContent>
                    </IonCard>

                    {/* Fiat Assets List */}
                        {supportedAssets
                            .filter((asset: Asset) => asset.type === 'fiat')
                            .map((asset: Asset) => {
                                const inAccount = !!balances?.[asset.identifier];
                                const balance = balances?.[asset.identifier];

                                return (
                                    <IonCard key={asset.identifier}>
                                        <IonCardContent>
                                            <IonItem lines="none">
                                                <IonThumbnail className="accountsPage_Thumbnail_Flag" slot="start">
                                                    <img src={asset.icon} alt={asset.display_code} />
                                                </IonThumbnail>

                                                <IonLabel className="ion-text-start">
                                                    <h4 className="m-0 ion-text-start">{asset.display_code}</h4>
                                                    <p className="text-sm text-gray-500 dark:text-slate-400 m-0 ion-text-start" style={{ textAlign: 'left' }}>
                                                        {inAccount && balance
                                                            ? `${balance.available.toFixed(2)} ${asset.code}`
                                                            : asset.code
                                                        }
                                                    </p>
                                                </IonLabel>

                                                {inAccount ? (
                                                    <div slot='end' className="flex gap-2 items-center">
                                                        <IonButton
                                                            onClick={() => goToRecipientPage(asset)}
                                                            fill='clear'
                                                            color={'primary'}
                                                        >
                                                            <RiSendPlaneLine size={24} />
                                                        </IonButton>

                                                        <IonButton
                                                            onClick={() => {presentQRReceiveModal(asset)}}
                                                            fill='clear'
                                                            color={'primary'}
                                                        >
                                                            <RiQrCodeLine size={24} />
                                                        </IonButton>
                                                    </div>
                                                ) : (
                                                    <IonButton
                                                        onClick={() => { }}
                                                        color={'primary'}
                                                        fill='outline'
                                                        slot='end'
                                                    >
                                                        {t('addAccount')}
                                                    </IonButton>
                                                )}
                                            </IonItem>
                                        </IonCardContent>
                                    </IonCard>
                                );
                            })}
                </div>
            </IonContent>
        </IonPage>
    );

};

const CashAccountPage: React.FC = () => (
    <Provider store={store}>
        <Page />
    </Provider>
);

export default CashAccountPage;
